import { Link } from "@tanstack/react-router";
import { SubFlowIcon } from "@signet/ui";
import { NowPanelHead, nowRangeVersus, nowRangeWords, useCreatorPhotos, useMe, useSeenQuery, type NowRange } from "@signet/web-kit";
import { BASE, dayLabel, fetchRange, subsKeys } from "./api.ts";
import { BandTabs, CountsOnly, FlowTable, PlatformSwitch, rowsFor, Summary, useBand, usePlatform } from "./board.tsx";
import { flowWords } from "./now-stat.tsx";
import { UrgentView } from "./urgent.tsx";

/* ──────────────────────────────────────────────────────────────────────────
   Sub flow on Now (the boss, 2026-10-13). It follows the page's range by date,
   except Today, which is the newest day with subs typed in, usually yesterday
   since the afternoon message reports the day before (Jay, 2026-10-15). The
   head names the day shown. Longer ranges add the days up and band a creator
   by their average day.
   ────────────────────────────────────────────────────────────────────────── */

export function SubsNowPanel({ range }: { range: NowRange }) {
  const flow = useSeenQuery({ queryKey: subsKeys.range(range), queryFn: () => fetchRange(range), refetchInterval: 60_000 });
  const photos = useCreatorPhotos(BASE);
  const { can } = useMe();
  const data = flow.data;
  // All, OnlyFans or Fansly, as on the Sub Flow page and remembered with it (Jay, 2026-10-13).
  const [platform, setPlatform] = usePlatform();
  const [band, setBand] = useBand(data, platform);
  const oneDay = range.key === "today" || range.key === "yesterday" || (range.key === "custom" && range.from === range.to);
  const chip = data?.noData ? (range.key === "yesterday" ? "No data for yesterday" : range.key === "today" ? "No data for today" : "No data for these days") : null;

  return (
    <section className="panel subs-now">
      <NowPanelHead
        icon={<SubFlowIcon size={12} />}
        label={data ? (data.from === data.to ? `Stats for ${dayLabel(data.from)}` : `Stats for ${dayLabel(data.from)} to ${dayLabel(data.to)}`) : `Subs ${nowRangeWords(range)}`}
        title={(() => {
          const words = flowWords(range, data);
          return words === "today" ? "Today's Sub Flow" : words === "yesterday" ? "Yesterday's Sub Flow" : `Sub Flow ${words}`;
        })()}
        sub={data && !data.noData ? <Summary flow={data} versus={nowRangeVersus(range)} /> : "New subs per creator, typed in each afternoon."}
        side={
          <span className="subs-head-actions">
            {chip ? <span className="signet-stat-chip">{chip}</span> : null}
            {can("subs.entry:view") ? (
              <Link to={"/subs/enter" as never} className="text-button tiny accent">
                Enter / edit subs
              </Link>
            ) : null}
          </span>
        }
      />
      <div className="panel-body">
        {flow.isError ? <p className="faint">Couldn't load Sub Flow.</p> : null}
        {data ? (
          <>
            <PlatformSwitch value={platform} onChange={setPlatform} />
            <BandTabs flow={data} band={band} onBand={setBand} platform={platform} />
            {band === "urgent" ? (
              <UrgentView day={data.to} photos={photos} />
            ) : (
              <>
                {band === "list" && !rowsFor(data, band, platform).length && data.countsOnly?.length ? null : <FlowTable flow={data} rows={rowsFor(data, band, platform)} photos={photos} oneDay={oneDay} />}
                {band === "list" ? <CountsOnly flow={data} photos={photos} /> : null}
              </>
            )}
            <Link to={"/subs" as never} className="text-button tiny accent">
              Open Sub Flow
            </Link>
          </>
        ) : null}
      </div>
    </section>
  );
}
